/**
 * Example Card Component
 *
 * Displays a single example file in a card format.
 */

import type { ExampleSearchResult } from '../types';
import { COMPLEXITY_LEVELS, EXAMPLE_CATEGORIES, HVAC_SYSTEM_TYPES } from '../types';

export interface ExampleCardProps {
  example: ExampleSearchResult;
  onSelect: () => void;
  onPreview: () => void;
  viewMode?: 'list' | 'grid';
}

export function ExampleCard({ example, onSelect, onPreview, viewMode = 'list' }: ExampleCardProps) {
  const complexityInfo = COMPLEXITY_LEVELS[example.complexity];
  const categoryInfo = EXAMPLE_CATEGORIES[example.category];

  const complexityColors: Record<string, string> = {
    beginner: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    intermediate: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    advanced: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    expert: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  };

  if (viewMode === 'grid') {
    return (
      <div className="group flex flex-col rounded-lg border bg-card p-4 transition-all hover:border-primary/50 hover:shadow-md">
        {/* Header */}
        <div className="mb-3 flex items-start justify-between">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <CategoryIcon category={example.category} />
          </div>
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${complexityColors[example.complexity]}`}
          >
            {complexityInfo.name}
          </span>
        </div>

        {/* Content */}
        <h3 className="mb-1 line-clamp-2 font-semibold">{example.name}</h3>
        <p className="mb-3 line-clamp-2 flex-1 text-sm text-muted-foreground">
          {example.description}
        </p>

        {/* Tags */}
        <div className="mb-3 flex flex-wrap gap-1">
          {example.hvacSystems?.slice(0, 2).map((sys) => (
            <span
              key={sys}
              className="rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
            >
              {HVAC_SYSTEM_TYPES[sys]?.name ?? sys}
            </span>
          ))}
          {example.stats && (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-800">
              {example.stats.zones} zones
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onPreview}
            className="flex-1 rounded-md border px-3 py-1.5 text-sm transition-colors hover:bg-muted"
          >
            Learn
          </button>
          <button
            onClick={onSelect}
            className="flex-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90"
          >
            Open
          </button>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="group flex items-center gap-4 rounded-lg border p-3 transition-colors hover:bg-muted/50">
      {/* Category icon */}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <CategoryIcon category={example.category} />
      </div>

      {/* Main content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate font-medium">{example.name}</h3>
          {example.popularity && example.popularity >= 85 && (
            <span className="shrink-0 text-amber-500" title="Popular example">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </span>
          )}
        </div>
        <p className="truncate text-sm text-muted-foreground">{example.description}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          {/* Complexity badge */}
          <span className={`rounded px-1.5 py-0.5 ${complexityColors[example.complexity]}`}>
            {complexityInfo.name}
          </span>

          {/* Category */}
          <span className="rounded bg-gray-100 px-1.5 py-0.5 dark:bg-gray-800">
            {categoryInfo.name}
          </span>

          {/* HVAC systems */}
          {example.hvacSystems?.slice(0, 2).map((sys) => (
            <span
              key={sys}
              className="rounded bg-purple-100 px-1.5 py-0.5 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
            >
              {HVAC_SYSTEM_TYPES[sys]?.name ?? sys}
            </span>
          ))}

          {/* Stats */}
          {example.stats && (
            <span className="text-muted-foreground">
              {example.stats.zones} zones
            </span>
          )}

          {/* Learning concepts count */}
          <span className="text-muted-foreground">
            {example.learning.concepts.length} concepts
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-2">
        <button
          onClick={onPreview}
          className="rounded-md px-2 py-1 text-sm text-muted-foreground transition-opacity hover:bg-muted hover:text-foreground sm:opacity-0 sm:group-hover:opacity-100"
          title="Preview and learn"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
          </svg>
        </button>
        <button
          onClick={onSelect}
          className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground hover:bg-primary/90"
        >
          Open
        </button>
      </div>
    </div>
  );
}

function CategoryIcon({ category }: { category: string }) {
  switch (category) {
    case 'hvac-systems':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2" />
          <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
          <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
        </svg>
      );
    case 'controls':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="21" x2="4" y2="14" />
          <line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" />
          <line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" />
          <line x1="9" y1="8" x2="15" y2="8" />
          <line x1="17" y1="16" x2="23" y2="16" />
        </svg>
      );
    case 'envelope':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
          <path d="m3.3 7 8.7 5 8.7-5" />
          <path d="M12 22V12" />
        </svg>
      );
    case 'lighting':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2" />
          <path d="M12 20v2" />
          <path d="m4.93 4.93 1.41 1.41" />
          <path d="m17.66 17.66 1.41 1.41" />
          <path d="M2 12h2" />
          <path d="M20 12h2" />
          <path d="m6.34 17.66-1.41 1.41" />
          <path d="m19.07 4.93-1.41 1.41" />
        </svg>
      );
    case 'renewables':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      );
    case 'refrigeration':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z" />
        </svg>
      );
    case 'water-systems':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z" />
        </svg>
      );
    case 'advanced':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <rect x="9" y="9" width="6" height="6" />
          <path d="M15 2v2" />
          <path d="M15 20v2" />
          <path d="M2 15h2" />
          <path d="M2 9h2" />
          <path d="M20 15h2" />
          <path d="M20 9h2" />
          <path d="M9 2v2" />
          <path d="M9 20v2" />
        </svg>
      );
    case 'tutorials':
    default:
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
      );
  }
}
